<template>
  <v-row class="section-container lh--patient-notification-container">
    <v-col cols="12" class="header-with-select-box">
      <h3 class="text-primary weight-600">Patient Outcome Notifications</h3>
    </v-col>

    <v-col cols="12">
      <v-checkbox
        id="patient-notification-enabled"
        v-model="notifyEnabled"
        :disabled="!props.isEditable"
        color="accent"
        hide-details
        label="Notify the Patient of the Prior Authorization outcome"
        @update:model-value="onToggle"></v-checkbox>

      <template v-if="notifyEnabled">
        <v-text-field
          id="patient-notification-contact"
          v-model="contact"
          :disabled="!props.isEditable"
          :rules="[contactRequiredRule]"
          label="Patient phone or email"
          class="mt-2"
          @update:model-value="onContactChange"></v-text-field>

        <p class="text-body-3-1 weight-600 text-primary mb-2 mt-2">Notify the Patient when the outcome is</p>
        <v-radio-group
          id="patient-notification-outcome"
          v-model="outcome"
          :disabled="!props.isEditable"
          color="accent"
          hide-details
          @update:model-value="onOutcomeChange">
          <v-radio
            v-for="option in PATIENT_NOTIFICATION_OUTCOME_OPTIONS"
            :key="option.value"
            :label="option.label"
            :value="option.value"></v-radio>
        </v-radio-group>
      </template>

      <v-alert type="info" variant="tonal">
        This preference is not present in every CoverMyMeds submission, so the patient won't necessarily be enrolled
        even when a contact is provided.
      </v-alert>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref } from "vue";
import {
  DEFAULT_PATIENT_NOTIFICATION_OUTCOME,
  PATIENT_NOTIFICATION_OUTCOME_OPTIONS,
} from "@/constants/patientNotification";

const emit = defineEmits(["updateOrderInfoField"]);

const props = defineProps({
  orderInfo: {
    type: Object,
    default() {
      return {};
    },
  },
  patientPhone: {
    type: String,
    default: "",
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

// The contact + outcome live on order_submission; a non-null contact is the opt-in signal.
const savedContact = props.orderInfo.orderSubmission?.patientNotificationContact ?? null;
const savedOutcome = props.orderInfo.orderSubmission?.patientNotificationOutcome ?? null;

const notifyEnabled = ref(Boolean(savedContact));
const contact = ref(savedContact ?? "");
const outcome = ref(savedOutcome ?? DEFAULT_PATIENT_NOTIFICATION_OUTCOME);

function contactRequiredRule(value) {
  return Boolean(value && value.trim()) || "Enter a phone or email to notify the patient";
}

function onToggle() {
  if (notifyEnabled.value) {
    // Explicit opt-in: prefill the patient's phone (if known) so the user only edits when needed.
    if (!contact.value) {
      contact.value = props.patientPhone || "";
    }
    emit("updateOrderInfoField", "patientNotificationOutcome", outcome.value);
    emit("updateOrderInfoField", "patientNotificationContact", contact.value || null);
  } else {
    contact.value = "";
    emit("updateOrderInfoField", "patientNotificationContact", null);
    emit("updateOrderInfoField", "patientNotificationOutcome", null);
  }
}

function onContactChange() {
  emit("updateOrderInfoField", "patientNotificationContact", contact.value || null);
}

function onOutcomeChange() {
  emit("updateOrderInfoField", "patientNotificationOutcome", outcome.value);
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
</style>
